import { CommonActionTypes, DMExplorer, IDmData, MessageBox } from '@bimser/common';
import dmInitializerAction from '@bimser/common/dist/action/dmInitializerAction';
import { BCNotification, BCScrollbars, findMessage } from '@bimser/components';
import { FormItemTypes, IDictionary, MLHelper, base64ToBlobData, createGuid } from '@bimser/core';
import IconMore from '@bimser/icons/16/more2';
import classNames from 'classnames/bind';
import Style from '../../../../assets/FileSelectorStyle.scss';
import CompositeDataBoundControl from '../../base/CompositeDataBoundControl';
import FileSelectorItem from './components/FileSelectorItem';
import IFile from './entities/IFile';
import IFileSelectorProperties from './entities/IFileSelectorProperties';

const cx = classNames.bind(Style);
const tempData = [
	{ id: '123', name: 'File1.pdf', path: 'UserFiles' },
	{ id: '124', name: 'File2.docx', path: 'UserFiles' }
];

class FileSelector extends CompositeDataBoundControl {
	allowSelectMultiple: boolean;
	allowRemove: boolean;
	selectionTargetPath: string;
	selectionTargetId: string;
	maxSelectionCount: number;
	uploadTargetPath: string;
	uploadTargetId: string;
	allowedFileExtensions: string[];
	allowView: boolean;
	files: IFile[];
	__uploadTargetSecretKey: string;
	__selectionTarget: IDmData;

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.FileSelector;

		this.__onClickBrowse = this.__onClickBrowse.bind(this);
		this.__openDMSelector = this.__openDMSelector.bind(this);
		this.__onFileAdded = this.__onFileAdded.bind(this);
		this.__onNewFilesAdded = this.__onNewFilesAdded.bind(this);
		this.__onFileSelected = this.__onFileSelected.bind(this);
		this.__onClickItem = this.__onClickItem.bind(this);
		this.__onRemove = this.__onRemove.bind(this);

		this.subscribe('trigger:OnFileSelected', (value: any) => {
			const mobileValue = JSON.parse(value);
			const mobileMlValue = mobileValue.map(item => ({
				...item,
				name: MLHelper.getMLText(item.name)
			}));
			const isDm: boolean = mobileMlValue.some(item => item.secretKey);

			if (isDm) {
				this.__onFileSelected({ dmData: mobileMlValue });
			} else {
				const mobileFiles = mobileMlValue.map(item => {
					const data = base64ToBlobData(item.data);
					const blob = new Blob([data], { type: item.mimeType });
					return new File([blob], item.name, { type: item.mimeType });
				});
				this.__onNewFilesAdded(mobileFiles);
			}
		});
		this.subscribe('trigger:RemoveFileSelected', (value: string) => {
			this.__onRemove(value);
		});
	}

	initialize(): void {
		super.initialize();

		this.__form.subscribe('event:DMFileUploaded', this.__onFileAdded);
	}

	__onFileAdded(data: any) {
		if (
			!(
				data?.payload?.windowId == this.__form.__windowId &&
				data.payload.controlId == this.name
			)
		) {
			return;
		}

		if (data.payload.success) {
			const tId = this.__form.beginTransaction();

			this.__proxyChanging.files = [
				...this.files,
				{
					id: data.payload.dmData.id,
					name: data.payload.dmData.name,
					path: data.payload.dmData.path,
					secretKey: data.payload.dmData.secretKey
				}
			];

			this.__form.endTransaction(tId);
		}
	}

	__onFileSelected(payload: { dmData: IDmData[] }) {
		const invalidFiles = [];
		const validFiles = payload.dmData.filter((t: any) => {
			const type = t.name.substr(t.name.lastIndexOf('.') + 1).toUpperCase();
			const result = this.allowedFileExtensions.length
				? this.allowedFileExtensions.includes(type)
				: true;
			if (!result) {
				invalidFiles.push(t.name);
			}
			return result;
		});

		if (this.allowedFileExtensions.length && invalidFiles.length) {
			BCNotification.warning({ message: findMessage.get('101357') });
		}

		const files = [...this.files];
		for (const dmObj of validFiles) {
			if (this.files.findIndex(f => f.id == dmObj.id) == -1) {
				files.push({
					id: dmObj.id,
					name: dmObj.name,
					path: dmObj.path,
					secretKey: dmObj.secretKey
				});
			}
		}
		this.__proxyChanging.files = files;
	}

	__loadProperties(data: IFileSelectorProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.allowSelectMultiple = data.allowSelectMultiple;
		this.__proxyChanged.allowRemove = data.allowRemove;
		this.__proxyChanged.maxSelectionCount =
			typeof data.maxSelectionCount === 'number'
				? Math.max(2, data.maxSelectionCount)
				: data.maxSelectionCount;
		this.__proxyChanged.selectionTargetId = data.selectionTargetId;
		this.__proxyChanged.selectionTargetPath = data.selectionTargetPath;
		this.__proxyChanged.uploadTargetId = data.uploadTargetId;
		this.__proxyChanged.uploadTargetPath = data.uploadTargetPath;
		this.__proxyChanged.allowedFileExtensions = data.allowedFileExtensions;
		this.__proxyChanged.allowView = data.allowView;
		this.__proxyChanged.files = data.files ?? [];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			allowSelectMultiple: this.allowSelectMultiple,
			allowRemove: this.allowRemove,
			maxSelectionCount: this.maxSelectionCount,
			selectionTargetPath: this.selectionTargetPath,
			selectionTargetId: this.selectionTargetId,
			uploadTargetPath: this.uploadTargetPath,
			uploadTargetSecretKey: this.__uploadTargetSecretKey,
			uploadTargetId: this.uploadTargetId,
			allowedFileExtensions: this.allowedFileExtensions,
			allowView: this.allowView,
			files: this.files
		};
	}

	async __onNewFilesAdded(files: File[]) {
		if (!files.length || !this.__uploadTargetSecretKey) {
			return;
		}

		return new Promise<void>((resolve, reject) => {
			this.__form.emit(
				'dispatchAction',
				dmInitializerAction({
					onCompleted: () => {
						this.__form.emit('dispatchAction', {
							type: CommonActionTypes.RELATED_DOCUMENTS_UPLOAD_FILE_ACTION,
							payload: {
								eventEmitter: true,
								controlId: this.name,
								windowId: this.__form.__windowId,
								destinationSecret: this.__uploadTargetSecretKey,
								files: files.map(f => ({ id: createGuid(), file: f })),
								targetId: this.uploadTargetId,
								onCompleted: () => {
									resolve();
								}
							}
						});
					}
				})
			);
		});
	}

	__onClickBrowse() {
		if (
			!(this.__isDisabled() || this.__isReadOnly()) &&
			this.__form.displayMode == 'execution'
		) {
			if (this.__uploadTargetSecretKey) {
				const { close } = this.__form.__interactionHelper({
					id: this.controlId + '_FileSelectorBrowse',
					type: 'modal',
					title: findMessage.get('101202'),
					contentComponentType: 'FileSelectorBrowse',
					width: this.__form.__fromMobile
						? `${this.__form.windowSize.width}px !important`
						: '400px',
					contentComponentProps: {
						onClickOpenDmSelector: this.__openDMSelector,
						allowMultiple: this.allowSelectMultiple,
						currentCount: this.files?.length || 0,
						maxSelectionCount: this.maxSelectionCount,
						allowedFileExtensions: this.allowedFileExtensions,
						onOK: (files: File[]) => {
							close();
							this.__proxyChanging.loading = true;
							this.__onNewFilesAdded(files).finally(() => {
								this.__proxyChanging.loading = false;
							});
						}
					},
					modalProps: {
						buttons: [],
						props: { footer: null }
					},
					onOk: () => {
						close();
					}
				});
			} else {
				this.__openDMSelector();
			}
		}
	}

	__getFileSelectorPath(): DMExplorer.Entities.INavigationHistoryItem {
		if (this.__selectionTarget) {
			return {
				secretKey: this.__selectionTarget.secretKey,
				id: this.__selectionTarget.id.toString(),
				name: MLHelper.getMLText(this.__selectionTarget.name)
			};
		}
		return null;
	}

	__openDMSelector() {
		const maxSelectionCount = this.maxSelectionCount
			? this.maxSelectionCount - this.files?.length
			: null;

		const currentPath = this.__getFileSelectorPath();

		const extensions = this.allowedFileExtensions.map(ext => ext.toLowerCase());

		const modalId = 'FS.ADD_ITEM_FROM_DM_SELECTOR';
		this.__form.__openedModalList.push(modalId);

		const { close } = this.__form.__interactionHelper({
			id: modalId,
			type: 'modal',
			title: extensions?.length ? findMessage.get('103605') : findMessage.get('103717'),
			contentComponentType: 'AddNewFileDMModal',
			contentComponentProps: {
				modalId,
				extensions: extensions.length > 0 ? extensions : ['*'],
				maxFileCount: this.allowSelectMultiple ? maxSelectionCount : undefined,
				currentPath,
				onOkEvent: async (items: DMExplorer.Entities.INavigationItem[]) => {
					this.__onFileSelected({
						dmData: items.map(item => item.baseItem)
					});

					MessageBox.closeModal(this.controlId + '_FileSelectorBrowse');
				}
			},
			modalProps: {
				props: {
					footer: null,
					disableScroll: true
				}
			},
			onOk: () => {
				close();
			}
		});
	}

	__onClickItem(itemKey: string) {
		if (!this.allowView) {
			return;
		}
		const file = this.files.find(f => f.id == itemKey);
		if (file?.secretKey && this.__form.displayMode == 'execution') {
			this.__form.emit(
				'dispatchAction',
				dmInitializerAction({
					onCompleted: () => {
						this.__form.emit('dispatchAction', {
							type: CommonActionTypes.RELATED_DOCUMENTS_VIEW_FILE,
							payload: {
								windowId: this.__form.__windowId,
								panelId: true,
								parentWindowType: this.__form.parentWindowType,
								secretKey: file.secretKey,
								fileName: file.name,
								itemId: file.id,
								openOn: this.__form.openOn || 'panel',
								maskClosable: false
							}
						});
					}
				})
			);
		}
	}

	__onRemove(itemKey: string) {
		if (this.__form.displayMode == 'execution') {
			this.__proxyChanging.files = this.files.filter(f => f.id !== itemKey);
		}
	}

	__fileMapper(file: IFile, isLoading: boolean = false) {
		return {
			key: file.id,
			text: file.path + '/' + file.name,
			isLoadingItem: isLoading
		};
	}

	__getFiles() {
		if (this.__form.displayMode == 'preview') {
			return tempData.map(f => this.__fileMapper(f));
		} else {
			return this.files.map(f => this.__fileMapper(f));
		}
	}

	__render() {
		const classname = cx({
			FileSelectorWrapper: true,
			disabled: this.__isDisabled()
		});

		const iconMoreClassname = cx({
			FileSelectorBrowse: true,
			Disabled: this.__isDisabled(),
			readonlyCursor: this.__isReadOnly()
		});

		return super.__render(
			<div
				className={classname}
				style={this.style}
			>
				<BCScrollbars
					styles={{
						width: '100%',
						minHeight: '40px',
						maxHeight: '80px'
					}}
					cssClass={Style.bcSrollableAreaForFileSelector}
				>
					{this.__getFiles().map(o => (
						<FileSelectorItem
							key={o.key}
							item={o}
							onClick={this.__onClickItem}
							onRemove={this.__onRemove}
							allowRemove={this.allowRemove}
							allowView={this.allowView}
							readOnly={this.__isDisabled() || this.__isReadOnly()}
						/>
					))}
				</BCScrollbars>
				<IconMore
					className={iconMoreClassname}
					width="32px"
					onClick={this.__onClickBrowse}
				/>
			</div>
		);
	}
}
export default FileSelector;
